import C from "@/modules/user/UserAvatar.module.css";

import { link } from "@/components/Action.tsx";
import { ActionMenuContent, type ActionMenuItemProps, WPopover } from "@/components/ActionMenu.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { showModal, waitModal } from "@/components/modal/registerModal.tsx";
import { accessImpersonationListQuery } from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { logoutAction } from "@/modules/api/logoutAction.ts";
import { useStandardQuery } from "@/modules/api/useStandardMutation.ts";
import { Isolated } from "@/modules/error/Isolated.tsx";
import { LogoutConfirmContent } from "@/modules/main/LogoutConfirmDialog.tsx";
import { useInAdmin } from "@/modules/main/adminContext.ts";
import { ActAsModalContent } from "@/modules/user/ActAsModal.tsx";
import { NameAvatar, UserAvatar } from "@/modules/user/UserAvatar.tsx";
import { stopImpersonatingAction } from "@/modules/user/impersonation.action.ts";
import { isImpersonatingAtom } from "@/modules/user/impersonation.store.ts";
import { profileLink } from "@/modules/user/profileLink.ts";
import { userDataAtom, userIsAdminAtom } from "@/modules/user/userApi.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import type { User } from "@warrenio/api-spec/spec.oats.gen";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useCallback, useMemo } from "react";
import { MenuTrigger } from "react-aria-components";
import { sortBy } from "remeda";

function useLogout() {
    const logout = useSetAtom(logoutAction);
    return useCallback(async () => {
        if (import.meta.env.PROD) {
            await waitModal(<LogoutConfirmContent />);
        } else {
            // For development - just hurry up and log out.
            await logout();
        }
    }, [logout]);
}

export function UserName() {
    const userData = useSuspenseQueryAtom(userDataAtom);

    const username = getUserName(userData);
    return (
        <>
            <UserAvatar inHeader user={userData} />
            <div className="ml-0.25rem hidden-mobile">{username}</div>
        </>
    );
}

function getUserName({ name, profile_data: { first_name, last_name } }: User) {
    return [first_name, last_name].filter(Boolean).join(" ") || name;
}

function UserMenuContent() {
    //#region Hooks
    const { data: impersonationData } = useStandardQuery(accessImpersonationListQuery);

    const logout = useLogout();
    const isImpersonating = useAtomValue(isImpersonatingAtom);
    const stopImpersonating = useSetAtom(stopImpersonatingAction);
    const isAdmin = useSuspenseQueryAtom(userIsAdminAtom);
    const isInAdminMode = useInAdmin();
    //#endregion

    const impersonationMenu = useMemo(() => {
        const impersonations = impersonationData
            ? sortBy([...impersonationData.values()], [(a) => a.created_at, "desc"])
            : [];

        const impersonationItems = impersonations
            .filter((item) => item.is_accepted)
            .map(
                (item): ActionMenuItemProps => ({
                    id: `impersonate_${item.uuid}`,
                    title: item.access_owner,
                    image: <NameAvatar name={item.access_owner} />,
                    action: () => {
                        showModal(<ActAsModalContent item={item} />);
                    },
                }),
            );

        return filterFalse<ActionMenuItemProps>([
            ...impersonationItems,
            impersonationItems.length > 0 && {
                id: "manage",
                title: <Trans>Manage</Trans>,
                icon: `jp-user-group-icon color-primary ${C.AvatarIconSize}`,
                action: profileLink,
            },
            isImpersonating && {
                id: "back_to_account",
                title: <Trans>Back to your account</Trans>,
                icon: `jp-exit-icon ${C.AvatarIconSize}`,
                action: stopImpersonating,
            },
        ]);
    }, [impersonationData, isImpersonating, stopImpersonating]);

    const items = useMemo(() => {
        return filterFalse<ActionMenuItemProps>([
            isInAdminMode
                ? {
                      id: "user_mode",
                      title: <Trans comment="Link to regular user interface">User view</Trans>,
                      icon: "jp-icon-home color-primary size-0.875rem",
                      action: link({ to: "/" }),
                  }
                : isAdmin && {
                      id: "admin_mode",
                      title: <Trans comment="Link to administrative user interface">Admin view</Trans>,
                      icon: "jp-admin-user-icon color-primary size-0.875rem",
                      action: link({ to: "/admin" }),
                  },
            {
                id: "profile",
                title: <Trans>Profile</Trans>,
                icon: "jp-user-icon color-primary size-0.875rem",
                action: profileLink,
            },
            {
                id: "act_as",
                title: <Trans comment="Menu item to impersonate another user">Act as</Trans>,
                icon: "jp-swap-icon color-primary size-0.875rem",
                childItems: impersonationMenu,
            },
            {
                id: "logout",
                title: (
                    <span className="color-error">
                        <Trans>Log out</Trans>
                    </span>
                ),
                icon: "jp-exit-icon color-error size-0.875rem",
                action: logout,
            },
        ]);
    }, [impersonationMenu, isInAdminMode, isAdmin, logout]);

    return <ActionMenuContent items={items} placement="bottom end" />;
}

export default function UserMenu() {
    // NB: Use `Isolated` so logout is still accessible even any of the queries fail.
    return (
        <MenuTrigger>
            {/*<UserSwitch />*/}

            <WButton color="primary" variant="ghost" size="bar" action={undefined}>
                <Isolated fallback="...">
                    <UserName />
                </Isolated>
                <MaskIcon className="ml-2 jp-icon-caretdown size-0.875rem" />
            </WButton>
            <WPopover>
                <UserMenuContent />
            </WPopover>
        </MenuTrigger>
    );
}
